/**
 * The Product Hunt award badge artwork: a 260×54 plate with the kitty mark,
 * two text lines, and ten rotating colour overlays blended over it. The
 * hover choreography (what drives `overlayPosition` and the two flags) stays
 * with the interactive badge in `components/marketing/award-badge.tsx`.
 */

const OVERLAY_COLORS = [
  'hsl(358, 100%, 62%)',
  'hsl(30, 100%, 50%)',
  'hsl(60, 100%, 50%)',
  'hsl(96, 100%, 50%)',
  'hsl(233, 85%, 47%)',
  'hsl(271, 85%, 47%)',
  'hsl(300, 20%, 35%)',
  'transparent',
  'transparent',
  'white',
]

const OVERLAY_ANIMATIONS = [...new Array(OVERLAY_COLORS.length).keys()]
  .map(
    (e) => `
    @keyframes overlayAnimation${e + 1} {
      0% {
        transform: rotate(${e * 10}deg);
      }
      50% {
        transform: rotate(${(e + 1) * 10}deg);
      }
      100% {
        transform: rotate(${e * 10}deg);
      }
    }
    `
  )
  .join(' ')

const KITTY_PATH =
  'M14.963 9.075c.787-3-.188-5.887-.188-5.887S12.488 5.175 11.7 8.175c-.787 3 .188 5.887.188 5.887s2.25-1.987 3.075-4.987m-4.5 1.987c.787 3-.188 5.888-.188 5.888S7.988 14.962 7.2 11.962c-.787-3 .188-5.887.188-5.887s2.287 1.987 3.075 4.987m.862 10.388s-.6-2.962-2.775-5.175C6.337 14.1 3.375 13.5 3.375 13.5s.6 2.962 2.775 5.175c2.213 2.175 5.175 2.775 5.175 2.775m3.3 3.413s-1.988-2.288-4.988-3.075-5.887.187-5.887.187 1.987 2.287 4.988 3.075c3 .787 5.887-.188 5.887-.188Zm6.75 0s1.988-2.288 4.988-3.075c3-.826 5.887.187 5.887.187s-1.988 2.287-4.988 3.075c-3 .787-5.887-.188-5.887-.188ZM32.625 13.5s-2.963.6-5.175 2.775c-2.213 2.213-2.775 5.175-2.775 5.175s2.962-.6 5.175-2.775c2.175-2.213 2.775-5.175 2.775-5.175M28.65 6.075s.975 2.887.188 5.887c-.826 3-3.076 4.988-3.076 4.988s-.974-2.888-.187-5.888c.788-3 3.075-4.987 3.075-4.987m-4.5 7.987s.975-2.887.188-5.887c-.788-3-3.076-4.988-3.076-4.988s-.974 2.888-.187 5.888c.788 3 3.075 4.988 3.075 4.988ZM18 26.1c.975-.225 3.113-.6 5.325 0 3 .788 5.063 3.038 5.063 3.038s-2.888.975-5.888.187a13 13 0 0 1-1.425-.525c.563.788 1.125 1.425 2.288 1.913l-.863 2.062c-2.063-.862-2.925-2.137-3.675-3.262-.262-.375-.525-.713-.787-1.05-.26.293-.465.586-.686.903l-.102.147-.048.068c-.775 1.108-1.643 2.35-3.627 3.194l-.862-2.062c1.162-.488 1.725-1.125 2.287-1.913-.45.225-.938.375-1.425.525-3 .788-5.887-.187-5.887-.187s1.987-2.288 4.987-3.075c2.212-.563 4.35-.188 5.325.037'

export type AwardBadgeIconProps = {
  fillColor: string
  eyebrowText: string
  eyebrowFontSize: number
  /** The award line, e.g. "Product of the Day #2". */
  label: string
  /** Base rotation (deg) the overlays fan out from; the hover offset. */
  overlayPosition: number
  /** Ease the overlays to a new position (hover enter / leave). */
  isOverlayTransitionEnabled: boolean
  /** Run the idle keyframe drift; off while the cursor drives the overlays. */
  isOverlayAnimationEnabled: boolean
}

export function AwardBadgeIcon({
  fillColor,
  eyebrowText,
  eyebrowFontSize,
  label,
  overlayPosition,
  isOverlayTransitionEnabled,
  isOverlayAnimationEnabled,
}: AwardBadgeIconProps) {
  return (
    <>
      <style>{OVERLAY_ANIMATIONS}</style>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 260 54"
        className="h-auto w-full"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <filter id="blur1">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
          </filter>
          <mask id="badgeMask">
            <rect width="260" height="54" fill="white" rx="10" />
          </mask>
        </defs>
        <rect width="260" height="54" rx="10" fill={fillColor} />
        <rect
          x="4"
          y="4"
          width="252"
          height="46"
          rx="8"
          fill="transparent"
          stroke="#bbb"
          strokeWidth="1"
        />
        <text
          fontFamily="Helvetica-Bold, Helvetica, Arial, sans-serif"
          fontSize={eyebrowFontSize}
          fontWeight="bold"
          fill="#666"
          x="53"
          y="20"
        >
          {eyebrowText}
        </text>
        <text
          fontFamily="Helvetica-Bold, Helvetica, Arial, sans-serif"
          fontSize="16"
          fontWeight="bold"
          fill="#666"
          x="52"
          y="40"
        >
          {label}
        </text>
        <g transform="translate(8, 9)">
          <path fill="#666" d={KITTY_PATH} />
        </g>
        <g style={{ mixBlendMode: 'overlay' }} mask="url(#badgeMask)">
          {OVERLAY_COLORS.map((color, i) => (
            <g
              // biome-ignore lint/suspicious/noArrayIndexKey: static decorative visual data — never reordered
              key={`overlay-${i}`}
              style={{
                transform: `rotate(${overlayPosition + i * 10}deg)`,
                transformOrigin: 'center center',
                transition: isOverlayTransitionEnabled
                  ? 'transform 200ms ease-out'
                  : 'none',
                animation: isOverlayAnimationEnabled
                  ? `overlayAnimation${i + 1} 5s infinite`
                  : 'none',
                willChange: 'transform',
              }}
            >
              <polygon
                points="0,0 260,54 260,0 0,54"
                fill={color}
                filter="url(#blur1)"
                opacity="0.35"
              />
            </g>
          ))}
        </g>
      </svg>
    </>
  )
}
